import { useCallback, useRef } from "react";
import { t } from "@/i18n-js/instance";
import { Typography } from "@/react/components/shared/uikit/Typography";
import { useChecklistApi } from "@/react/hooks/useChecklistApi";
import { useCreateFirstBroadcast } from "@/react/hooks/useCreateFirstBroadcast";
import { stepKeys } from "@/react/types/Checklist";
import {
  ModalManager,
  useModal,
} from "@circle-react/components/Layout/ModalManagerProvider";
import { Button } from "@circle-react-uikit/Button";
import { Modal } from "@circle-react-uikit/ModalV2";
import { EmailBroadcastSurveyForm } from "./EmailBroadcastSurveyForm";

const localeRoot =
  "community_onboarding.new_checklist.email_broadcast_survey_modal";

const EmailBroadcastSurveyModalComponent = () => {
  const { createFirstBroadcast } = useCreateFirstBroadcast();
  const { updateChecklist } = useChecklistApi();
  const modal = useModal();
  const triggerFormSubmitRef = useRef<(() => Promise<void>) | null>(null);

  const handleOnClose = useCallback(async () => {
    await modal.hide();
    modal.remove();
  }, [modal]);

  const handleFormSubmit = useCallback(async () => {
    updateChecklist({ [stepKeys.send_launch_announcement]: true });
    await handleOnClose();
  }, [updateChecklist, handleOnClose]);

  const handleSubmitClick = useCallback(async () => {
    if (triggerFormSubmitRef.current) {
      await triggerFormSubmitRef.current();
    }
  }, []);

  const handleCreateBroadcast = useCallback(async () => {
    await createFirstBroadcast();
    await handleOnClose();
  }, [createFirstBroadcast, handleOnClose]);

  return (
    <Modal isOpen={modal.visible} onClose={handleOnClose}>
      <Modal.Overlay />
      <Modal.Content size="md">
        <Modal.Header>
          <Modal.Title size="sm">{t(`${localeRoot}.title`)}</Modal.Title>
          <Modal.HeaderActions>
            <Modal.CloseButton onClick={handleOnClose} />
          </Modal.HeaderActions>
        </Modal.Header>
        <Modal.Body>
          <Typography.BodyMd color="text-default">
            {t(`${localeRoot}.description`)}
          </Typography.BodyMd>

          <EmailBroadcastSurveyForm
            onSubmit={handleFormSubmit}
            triggerFormSubmitRef={triggerFormSubmitRef}
          />
        </Modal.Body>
        <Modal.Footer>
          <div className="flex items-stretch justify-end space-x-2">
            <Button onClick={handleCreateBroadcast} variant="secondary">
              {t(`${localeRoot}.footer.create_broadcast`)}
            </Button>

            <Button onClick={handleSubmitClick} variant="primary">
              {t(`${localeRoot}.submit`)}
            </Button>
          </div>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
};

const EmailBroadcastSurveyModal = ModalManager.create(() => (
  <EmailBroadcastSurveyModalComponent />
));

export const useEmailBroadcastSurveyModal = () =>
  useModal(EmailBroadcastSurveyModal);
